/**
 * useWorkspace -- Zustand store for multi-terminal workspace management.
 *
 * Manages panel layout, presets, saved layouts, and panel swapping.
 * Persists saved layouts to localStorage.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { WorkspaceLayout, PanelConfig, WorkspacePreset, SavedLayout } from '@/lib/types'

// ── Layout grid definitions ──────────────────────────────────────────────

export const LAYOUT_CONFIGS: Record<WorkspaceLayout, { rows: number; cols: number; count: number; label: string }> = {
  '1x1': { rows: 1, cols: 1, count: 1, label: 'Single' },
  '1x2': { rows: 2, cols: 1, count: 2, label: '2 Row' },
  '2x1': { rows: 1, cols: 2, count: 2, label: '2 Column' },
  '1x3': { rows: 1, cols: 3, count: 3, label: '3 Column' },
  '2x2': { rows: 2, cols: 2, count: 4, label: '2x2 Grid' },
  '2x3': { rows: 2, cols: 3, count: 6, label: '2x3 Grid' },
  '3x3': { rows: 3, cols: 3, count: 9, label: '3x3 Grid' },
}

// ── Preset workspace configurations ──────────────────────────────────────

export const WORKSPACE_PRESETS: WorkspacePreset[] = [
  {
    name: 'Market Overview',
    layout: '2x2',
    panels: [
      { id: 'p1', terminalId: 't-mo-1', initialCommand: '/indices' },
      { id: 'p2', terminalId: 't-mo-2', initialCommand: '/forex' },
      { id: 'p3', terminalId: 't-mo-3', initialCommand: '/crypto' },
      { id: 'p4', terminalId: 't-mo-4', initialCommand: '/fear' },
    ],
  },
  {
    name: 'UK Focus',
    layout: '2x2',
    panels: [
      { id: 'p1', terminalId: 't-uk-1', initialCommand: '/ftse100' },
      { id: 'p2', terminalId: 't-uk-2', initialCommand: '/quote GBPUSD' },
      { id: 'p3', terminalId: 't-uk-3', initialCommand: '/news' },
      { id: 'p4', terminalId: 't-uk-4', initialCommand: '/earnings' },
    ],
  },
  {
    name: 'US Equities',
    layout: '2x2',
    panels: [
      { id: 'p1', terminalId: 't-us-1', initialCommand: '/sp500' },
      { id: 'p2', terminalId: 't-us-2', initialCommand: '/nasdaq' },
      { id: 'p3', terminalId: 't-us-3', initialCommand: '/screener' },
      { id: 'p4', terminalId: 't-us-4', initialCommand: '/wsb' },
    ],
  },
  {
    name: 'Risk Monitor',
    layout: '2x2',
    panels: [
      { id: 'p1', terminalId: 't-rm-1', initialCommand: '/risk' },
      { id: 'p2', terminalId: 't-rm-2', initialCommand: '/stress' },
      { id: 'p3', terminalId: 't-rm-3', initialCommand: '/pot' },
      { id: 'p4', terminalId: 't-rm-4', initialCommand: '/portfolio' },
    ],
  },
  {
    name: 'Alt Data',
    layout: '2x2',
    panels: [
      { id: 'p1', terminalId: 't-ad-1', initialCommand: '/congress' },
      { id: 'p2', terminalId: 't-ad-2', initialCommand: '/insider AAPL' },
      { id: 'p3', terminalId: 't-ad-3', initialCommand: '/shorts' },
      { id: 'p4', terminalId: 't-ad-4', initialCommand: '/sentiment NVDA' },
    ],
  },
  {
    name: 'UK Markets (6-panel)',
    layout: '2x3',
    panels: [
      { id: 'p1', terminalId: 't-ukf-1', initialCommand: '/ftse100' },
      { id: 'p2', terminalId: 't-ukf-2', initialCommand: '/quote GBPUSD' },
      { id: 'p3', terminalId: 't-ukf-3', initialCommand: '/quote SHEL.L' },
      { id: 'p4', terminalId: 't-ukf-4', initialCommand: '/quote BARC.L' },
      { id: 'p5', terminalId: 't-ukf-5', initialCommand: '/quote LLOY.L' },
      { id: 'p6', terminalId: 't-ukf-6', initialCommand: '/bonds' },
    ],
  },
  {
    name: 'Simon Portfolio',
    layout: '2x3',
    panels: [
      { id: 'p1', terminalId: 't-sp-1', initialCommand: '/portfolio' },
      { id: 'p2', terminalId: 't-sp-2', initialCommand: '/quote MSFT' },
      { id: 'p3', terminalId: 't-sp-3', initialCommand: '/quote NVDA' },
      { id: 'p4', terminalId: 't-sp-4', initialCommand: '/quote BLK' },
      { id: 'p5', terminalId: 't-sp-5', initialCommand: '/risk' },
      { id: 'p6', terminalId: 't-sp-6', initialCommand: '/pnl' },
    ],
  },
  {
    name: 'Full Command Center',
    layout: '3x3',
    panels: [
      { id: 'p1', terminalId: 't-cc-1', initialCommand: '/indices' },
      { id: 'p2', terminalId: 't-cc-2', initialCommand: '/ftse100' },
      { id: 'p3', terminalId: 't-cc-3', initialCommand: '/crypto' },
      { id: 'p4', terminalId: 't-cc-4', initialCommand: '/forex' },
      { id: 'p5', terminalId: 't-cc-5', initialCommand: '/portfolio' },
      { id: 'p6', terminalId: 't-cc-6', initialCommand: '/risk' },
      { id: 'p7', terminalId: 't-cc-7', initialCommand: '/fear' },
      { id: 'p8', terminalId: 't-cc-8', initialCommand: '/congress' },
      { id: 'p9', terminalId: 't-cc-9', initialCommand: '/commodities' },
    ],
  },
]

// ── Helper: generate default panels for a layout ─────────────────────────

function generatePanels(layout: WorkspaceLayout): PanelConfig[] {
  const count = LAYOUT_CONFIGS[layout].count
  return Array.from({ length: count }, (_, i) => ({
    id: `panel-${i}`,
    terminalId: `term-${Date.now()}-${i}`,
  }))
}

// ── Store ────────────────────────────────────────────────────────────────

interface WorkspaceState {
  layout: WorkspaceLayout
  panels: PanelConfig[]
  presets: WorkspacePreset[]
  savedLayouts: Record<string, SavedLayout>
  maximizedPanel: string | null
  lastWorkspaceName: string | null

  switchLayout: (layout: WorkspaceLayout) => void
  loadPreset: (name: string) => void
  saveLayout: (name: string) => void
  loadSavedLayout: (name: string) => void
  deleteLayout: (name: string) => void
  swapPanels: (a: string, b: string) => void
  updatePanelCommand: (panelId: string, command: string) => void
  updatePanelAutoRefresh: (panelId: string, interval: number | undefined) => void
  maximizePanel: (panelId: string | null) => void
  closePanel: (panelId: string) => void

  // FDC3-style context: broadcast a ticker to all panels
  focusedTicker: string | null
  setFocusedTicker: (ticker: string | null) => void
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      layout: '2x2' as WorkspaceLayout,
      panels: generatePanels('2x2'),
      presets: WORKSPACE_PRESETS,
      savedLayouts: {},
      maximizedPanel: null,
      lastWorkspaceName: null,

      switchLayout: (layout: WorkspaceLayout) => {
        const current = get().panels
        const needed = LAYOUT_CONFIGS[layout].count
        let panels: PanelConfig[]

        if (current.length >= needed) {
          panels = current.slice(0, needed)
        } else {
          panels = [
            ...current,
            ...Array.from({ length: needed - current.length }, (_, i) => ({
              id: `panel-${current.length + i}`,
              terminalId: `term-${Date.now()}-${current.length + i}`,
            })),
          ]
        }
        set({ layout, panels, maximizedPanel: null })
      },

      loadPreset: (name: string) => {
        const preset = get().presets.find(p => p.name === name)
        if (!preset) return
        // Deep-clone panels with fresh terminal IDs to avoid conflicts
        const panels = preset.panels.map((p, i) => ({
          ...p,
          id: `panel-${i}`,
          terminalId: `term-${Date.now()}-${i}`,
          initialCommand: p.initialCommand,
        }))
        set({ layout: preset.layout, panels, maximizedPanel: null })
      },

      saveLayout: (name: string) => {
        const { layout, panels } = get()
        const saved: SavedLayout = {
          name,
          layout,
          panels: panels.map(p => ({ ...p })),
          createdAt: new Date().toISOString(),
        }
        set(s => ({
          savedLayouts: { ...s.savedLayouts, [name]: saved },
          lastWorkspaceName: name,
        }))
      },

      loadSavedLayout: (name: string) => {
        const saved = get().savedLayouts[name]
        if (!saved) return
        const panels = saved.panels.map((p, i) => ({
          ...p,
          id: `panel-${i}`,
          terminalId: `term-${Date.now()}-${i}`,
        }))
        set({ layout: saved.layout, panels, maximizedPanel: null, lastWorkspaceName: name })
      },

      deleteLayout: (name: string) => {
        set(s => {
          const { [name]: _, ...rest } = s.savedLayouts
          return { savedLayouts: rest }
        })
      },

      swapPanels: (a: string, b: string) => {
        set(s => {
          const panels = [...s.panels]
          const idxA = panels.findIndex(p => p.id === a)
          const idxB = panels.findIndex(p => p.id === b)
          if (idxA === -1 || idxB === -1) return s
          ;[panels[idxA], panels[idxB]] = [panels[idxB], panels[idxA]]
          // Keep panel IDs matching positions
          panels[idxA] = { ...panels[idxA], id: a }
          panels[idxB] = { ...panels[idxB], id: b }
          return { panels }
        })
      },

      updatePanelCommand: (panelId: string, command: string) => {
        set(s => ({
          panels: s.panels.map(p =>
            p.id === panelId ? { ...p, initialCommand: command } : p
          ),
        }))
      },

      updatePanelAutoRefresh: (panelId: string, interval: number | undefined) => {
        set(s => ({
          panels: s.panels.map(p =>
            p.id === panelId ? { ...p, autoRefresh: interval } : p
          ),
        }))
      },

      maximizePanel: (panelId: string | null) => {
        set({ maximizedPanel: panelId })
      },

      closePanel: (panelId: string) => {
        set(s => {
          const panels = s.panels.filter(p => p.id !== panelId)
          // If no panels left, keep at least one
          if (panels.length === 0) {
            panels.push({
              id: 'panel-0',
              terminalId: `term-${Date.now()}-0`,
            })
          }
          return { panels }
        })
      },

      // FDC3-style context sharing — set a ticker that all panels can react to
      focusedTicker: null,
      setFocusedTicker: (ticker: string | null) => set({ focusedTicker: ticker }),
    }),
    {
      name: 'av-workspaces',
      partialize: (state) => ({
        layout: state.layout,
        panels: state.panels,
        savedLayouts: state.savedLayouts,
        lastWorkspaceName: state.lastWorkspaceName,
      }),
    },
  ),
)

export function useWorkspace() {
  return useWorkspaceStore()
}
